"use client";

import { useState } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Minus, Plus } from "lucide-react";
import { useCart } from "@/contexts/cart-context";
import { TextScramble } from "@/components/ui/text-scramble";
import CheckoutForm from "@/components/CheckoutForm";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import OrderConfirmation from "./OrderConfirmation";

interface CartProps {
  onClose?: () => void;
  isSheet?: boolean;
}

const saveOrderToLocalStorage = (orderId: string) => {
  const existingOrders = JSON.parse(localStorage.getItem("orders") || "[]");
  const newOrder = { id: orderId, date: new Date().toISOString() };
  localStorage.setItem("orders", JSON.stringify([...existingOrders, newOrder]));
};

export function Cart({ onClose, isSheet = false }: CartProps) {
  const [isCheckout, setIsCheckout] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const {
    cart,
    removeFromCart,
    updateCartItemQuantity,
    updateCartItemSize,
    getTotalItems,
    getTotalPrice,
  } = useCart();

  const totalItems = getTotalItems();
  const totalPrice = getTotalPrice();

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setIsCheckout(false);
    setOrderId(null);
  };

  const handleOrderPlaced = (newOrderId: string) => {
    setOrderId(newOrderId);
    saveOrderToLocalStorage(newOrderId);
  };

  return (
    <div className={`${isSheet ? "" : "container mx-auto px-4 py-8"}`}>
      {orderId ? (
        <OrderConfirmation orderId={orderId} onClose={handleClose} />
      ) : isCheckout ? (
        <CheckoutForm
          onBack={() => setIsCheckout(false)}
          cart={cart}
          onOrderPlaced={handleOrderPlaced}
          isSheet={isSheet}
        />
      ) : (
        <>
          {cart.length === 0 ? (
            <div
              onClick={onClose}
              className="h-[90vh] w-full flex flex-col justify-center items-center text-center gap-4"
            >
              <TextScramble
                className="font-mono uppercase text-sm"
                characterSet=". "
              >
                Your cart is empty...
              </TextScramble>
              <Link href={"/products"}>
                <Button size={"sm"}>See Products</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {cart.map((item) => (
                  <div
                    key={`${item.id}-${item.size}`}
                    className="flex items-center space-x-4 border-b pb-4"
                  >
                    <Image
                      src={item.media[0] || "/placeholder.svg"}
                      alt={item.name}
                      width={80}
                      height={80}
                      className="rounded-md"
                    />
                    <div className="flex-1">
                      <TextScramble className="font-semibold" characterSet=". ">
                        {item.name}
                      </TextScramble>
                      <p className="text-sm text-muted-foreground">
                        {item.price} TND
                      </p>
                      <div className="flex items-center mt-2 space-x-4">
                        <div className="flex items-center">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              updateCartItemQuantity(
                                item.id,
                                item.size,
                                Math.max(1, item.quantity - 1)
                              )
                            }
                            disabled={item.quantity === 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="mx-2">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              updateCartItemQuantity(
                                item.id,
                                item.size,
                                item.quantity + 1
                              )
                            }
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {item.sizes && item.sizes.length > 0 && (
                          <Select
                            value={item.size}
                            onValueChange={(newSize) =>
                              updateCartItemSize(item.id, item.size, newSize)
                            }
                          >
                            <SelectTrigger className="w-[80px]">
                              <SelectValue placeholder="Size" />
                            </SelectTrigger>
                            <SelectContent>
                              {item.sizes.map((size) => (
                                <SelectItem key={size} value={size}>
                                  {size}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => removeFromCart(item.id, item.size)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <p className="text-xl font-semibold">
                  Total Items: {totalItems}
                </p>
                <p className="text-xl font-semibold">
                  Total Price: {totalPrice} TND
                </p>
                <div className="mt-4 space-y-2">
                  <Button
                    className="w-full"
                    onClick={() => setIsCheckout(true)}
                  >
                    {isSheet ? "Checkout" : "Proceed to Checkout"}
                  </Button>
                  {isSheet ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleClose}
                    >
                      Continue Shopping
                    </Button>
                  ) : (
                    <Link href="/products">
                      <Button variant="outline" className="w-full">
                        Continue Shopping
                      </Button>
                    </Link>
                  )}
                  {isSheet && (
                    <Link href="/cart" passHref>
                      <Button
                        variant="link"
                        className="w-full"
                        onClick={handleClose}
                      >
                        View Cart
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
